/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { addBreadcrumb } from '@sentry/react';
import * as _ from 'lodash-es';
import type { Socket } from 'phoenix';
import authTokenUtil from './authTokenUtil';
import visitorAuthTokenUtil from './visitorAuthTokenUtil';
import { purgeApolloCache } from '../app/apollo/cache';
import { updateApolloLink } from '../app/apollo/getApolloClient';
import type { AppThunk } from '../app/redux/getReduxStore';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { Organization } from '../graphql/generated/graphql';
import {
  AuthenticationMode,
  BillingSummaryStatus,
  PlanTier,
  UserType,
} from '../graphql/generated/graphql';
import { UIActions } from '../ui/uiSlice';
import { initAnalytics } from '../utils/analytics';
import LoggedInTeams from '../utils/cookie/LoggedInTeams';
import { isElectron } from '../utils/desktopClient';
import intercomFacade from '../utils/intercomFacade';
import Logger from '../utils/Logger';
import { purgePersisted } from '../utils/persistRedux';
import SlabSocket from '../utils/SlabSocket';

const initialOrganization = {
  id: '',
  email: '',
  host: '',
  effectiveHost: '',
  name: '',
  logo: { thumb: null },
  planTier: PlanTier.Startup,
  periodEndAt: null,
  trialEndAt: null,
  isTrialEnded: false,
  isTrialEndingSoon: false,
  isLocked: false,
  billingStatus: BillingSummaryStatus.Active,
  settings: {
    authentication: AuthenticationMode.SsoAndPassword,
    seatLimit: null,
  },
  isVerified: false,
};

const initialUser = {
  id: '',
  name: '',
  avatar: {
    thumb: null,
    original: null,
  },
  title: '',
  type: UserType.User,
  email: '',
  settings: {
    notifications: {
      direct: { email: false, slack: false },
      participant: { email: false, slack: false },
      team: { email: false, slack: false },
    },
  },
  membershipGroups: [],
  identities: [],
  description: null,
};

type SessionState = {
  organization: Pick<Organization, keyof typeof initialOrganization>;
  user: Pick<
    DocumentType<typeof START_SESSION_SESSION_FRAGMENT>['user'],
    keyof typeof initialUser
  >;
  socket?: Socket;
};

const initialState: SessionState = {
  organization: initialOrganization,
  user: initialUser,
  socket: undefined,
};

// eslint-disable-next-line slab-graphql/no-unused-fields
const SET_USER_SETTING_USER_SETTING_FRAGMENT = gql(`
  fragment setUserSetting_userSetting on UserSettings {
    allowAccountAccess
    uiTheme
    aiAutofix
    aiPredict
    aiAsk
    preferDesktop
    notifications {
      direct {
        email
        slack
      }
      participant {
        email
        slack
      }
      team {
        email
        slack
      }
    }
  }
`);

const sessionSlice = createSlice({
  name: 'sessionSlice',
  initialState,
  reducers: {
    sessionStart(state, action: PayloadAction<{ socket: Socket }>) {
      state.socket = action.payload.socket;
    },
    visitorSessionStart(state, action: PayloadAction<{ socket: Socket }>) {
      state.socket = action.payload.socket;
    },
    setCurrentOrganization(
      state,
      action: PayloadAction<
        Partial<
          DocumentType<typeof START_SESSION_SESSION_FRAGMENT>['organization']
        >
      >,
    ) {
      state.organization = { ...state.organization, ...action.payload };
    },
    setCurrentUser(
      state,
      action: PayloadAction<
        DocumentType<typeof START_SESSION_SESSION_FRAGMENT>['user']
      >,
    ) {
      state.user = { ...state.user, ...action.payload };
    },
    setUserSetting(
      state,
      action: PayloadAction<
        DocumentType<typeof SET_USER_SETTING_USER_SETTING_FRAGMENT>
      >,
    ) {
      state.user = {
        ...state.user,
        settings: _.merge({}, state.user.settings, action.payload),
      };
    },
  },
});

const initSocket = (authToken: string) => {
  const socket = new SlabSocket(authToken);

  if (process.env.NODE_ENV === 'development') {
    // @ts-expect-error
    window.socket = socket;
  }

  socket.onClose((event) => {
    addBreadcrumb({
      category: 'socket.close',
      message: `closed with code ${event.code}`,
      level: 'warning',
    });
  });

  socket.onError(() => {
    addBreadcrumb({
      category: 'socket.error',
      level: 'error',
    });
  });

  socket.onOpen(() => {
    addBreadcrumb({
      category: 'socket.open',
      level: 'info',
    });
  });

  socket.connect();
  return socket;
};

const clearSession = (): AppThunk => (__, getState) => {
  const { session } = getState();
  authTokenUtil.remove();

  if (session.organization.id) {
    LoggedInTeams.remove(session.organization);
  } else if (isElectron) {
    LoggedInTeams.removeAll();
  }

  purgePersisted();
  purgeApolloCache();
  if (session.socket) {
    session.socket.disconnect();
  }
  updateApolloLink({ authToken: null, socket: null });

  analytics.ready(() => {
    analytics.reset();
    window.Intercom?.('shutdown');
  });
};

const INSTALL_VENDORS_ORGANIZATION_FRAGMENT = gql(`
  fragment installVendors_organization on Organization {
    id
    host
    planTier
    insertedAt
    subscriptionId
    ...initAnalytics_organization
  }
`);

const INSTALL_VENDORS_USER_FRAGMENT = gql(`
  fragment installVendors_user on User {
    id
    name
    email
    title
    type
    insertedAt
    ...initAnalytics_user
  }
`);

function installVendors({
  organization,
  user,
  intercomHash,
}: {
  organization: DocumentType<typeof INSTALL_VENDORS_ORGANIZATION_FRAGMENT>;
  user: DocumentType<typeof INSTALL_VENDORS_USER_FRAGMENT>;
  intercomHash: string;
}) {
  try {
    Logger.setContext(
      {
        id: user.id,
        name: user.name,
        email: user.email,
        title: user.title,
        type: _.toLower(user.type ?? ''),
        createdAt: user.insertedAt,
        sessionStartedAt: new Date().toISOString(),
      },
      {
        id: organization.id,
        host: organization.host,
        plan: organization.planTier ?? undefined,
        createdAt: organization.insertedAt,
        subscriptionId: organization.subscriptionId ?? undefined,
      },
    );
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
  initAnalytics(organization, user, intercomHash);
}

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments, slab-graphql/no-unused-fields
const START_SESSION_SESSION_FRAGMENT = gql(`
  fragment startSession_session on Session {
    authToken
    intercomHash
    organization {
      id
      insertedAt
      logo {
        thumb
      }
      name
      email
      host
      effectiveHost
      isVerified
      settings {
        authentication
        seatLimit
      }
      planTier
      periodEndAt
      trialEndAt
      isTrialEnded
      isTrialEndingSoon
      isLocked
      billingStatus
      ...installVendors_organization
    }
    user {
      id
      email
      name
      title
      description
      type
      insertedAt
      membershipGroups {
        id
      }
      identities {
        id
        platform
      }
      settings {
        ...setUserSetting_userSetting
      }
      ...installVendors_user
      ...UserAvatar
    }
  }
`);

const startSession =
  ({
    authToken,
    intercomHash,
    organization,
    user,
  }: DocumentType<typeof START_SESSION_SESSION_FRAGMENT>): AppThunk =>
  (dispatch) => {
    authTokenUtil.set(authToken);
    const socket = initSocket(authToken);
    updateApolloLink({ authToken, socket });

    LoggedInTeams.add(organization);

    intercomFacade.onError(() => {
      dispatch(
        UIActions.pushToast({
          message: 'Unable to load messaging, possibly due to Adblock.',
          primaryText: 'Use support form',
          primaryTo: `https://go.slab.com/slab-contact-us`,
          primaryTarget: '_blank',
          level: 'error',
        }),
      );
    });

    installVendors({ organization, user, intercomHash });
    dispatch(sessionSlice.actions.setCurrentOrganization(organization));
    dispatch(sessionSlice.actions.setCurrentUser(user));
    dispatch(sessionSlice.actions.sessionStart({ socket }));
    return Promise.resolve();
  };

const START_VISITOR_SESSION_SESSION_FRAGMENT = gql(`
  fragment startVisitorSession_session on VisitorSession {
    authToken
    user {
      id
      name
    }
  }
`);

const startVisitorSession =
  ({
    authToken,
    user,
  }: DocumentType<typeof START_VISITOR_SESSION_SESSION_FRAGMENT>): AppThunk =>
  (dispatch) => {
    visitorAuthTokenUtil.set(authToken);
    const socket = initSocket(authToken);
    updateApolloLink({ authToken, socket });

    dispatch(
      sessionSlice.actions.setCurrentUser({
        ...initialUser,
        title: '',
        insertedAt: '',
        id: user.id,
        name: user.name,
        type: UserType.Visitor,
      }),
    );

    dispatch(sessionSlice.actions.visitorSessionStart({ socket }));
  };

export const SessionActions = {
  clearSession,
  startSession,
  startVisitorSession,
  ...sessionSlice.actions,
};

export default sessionSlice.reducer;
